import { Identifiable, PointArray, Toggleable } from '@editor/interfaces/attribute'
import { CircleAnchorIcon } from '@infraloop/common'
import { ThreeEvent } from '@react-three/fiber'
import { OrthographicCamera, PerspectiveCamera } from 'three'

export enum AnchorColors {
  Normal = '#333',
  Drawing = 'red',
  Selected = 'orange',
  Movable = '#248CE4',
}

export interface CircleAnchorProps extends Identifiable, Toggleable {
  point: PointArray
  scale?: number
  hoverScale?: number

  /**
   * Use an icon as label on the anchor itself.
   */
  icon?: CircleAnchorIcon

  /**
   * Color of the icon.
   */
  iconColor?: string

  /**
   * Scale of the icon.
   */
  iconScale?: number

  /**
   * Inner circle color.
   */
  color?: string

  /**
   * Color for the outer circle.
   */
  outlineColor?: string

  /**
   * Scale for the inner circle.
   * This is relative to the size of the outer circle (`scale` prop).
   */
  innerScale?: number

  /**
   * Opacity for the outer circle.
   */
  outlineOpacity?: number

  /**
   * Opacity for the inner circle.
   * Also applies to the tooltip.
   */
  opacity?: number

  /**
   * Multiplier for the touch/click target area.
   * Creates an invisible larger area around the anchor that's easier to tap.
   * @default 1.5
   */
  touchAreaMultiplier?: number

  depthTest?: boolean
  depthWrite?: boolean
  renderOrder?: number

  /**
   * Whether the anchor can be interacted with.
   */
  isInteractable?: boolean

  onDown?: (event: ThreeEvent<PointerEvent>) => void
  onUp?: (event: ThreeEvent<PointerEvent>) => void
  onEnter?: (event: ThreeEvent<PointerEvent>) => void
  onLeave?: (event: ThreeEvent<PointerEvent>) => void
  onMove?: (event: ThreeEvent<PointerEvent>) => void

  /**
   * Tooltip when hovered.
   */
  tooltipText?: string

  /**
   * When to show the tooltip.
   * Defaults to 'hover'.
   */
  showTooltip?: 'hover' | 'always'

  /**
   * Custom camera.
   */
  customCamera?: PerspectiveCamera | OrthographicCamera
}
